import React from 'react'
import { useQuery } from '@apollo/client'
import { Box, Chip, Typography, CircularProgress, Tooltip } from '@mui/material'
import Link from 'gui/components/link'
import { useTheme } from '@mui/material/styles'
import { getLanguageOptionsSupported, getLanguageChipClass } from 'services/languages'
import { GET_CONTENT_TRANSLATIONS } from './queries'
import './translations.scss'

interface TranslationsProps {
  contentId: number
  contentType: string
  currentLanguage: string
  primaryLanguage?: string | null
  isViewOnly?: boolean
}

function Translations ({
  contentId,
  contentType,
  currentLanguage,
  primaryLanguage,
  isViewOnly = false,
}: TranslationsProps) {
  const theme = useTheme()

  const { loading, error, data } = useQuery(GET_CONTENT_TRANSLATIONS, {
    variables: {
      contentId,
      contentType,
    },
    skip: !contentId || !contentType,
  })

  const translations = React.useMemo(() => {
    if (!data?.contentList?.content) return []
    return data.contentList.content.filter(
      (item: { id?: string; language?: string }) =>
        item?.language && item.language !== currentLanguage,
    )
  }, [data, currentLanguage])

  const languageOptions = React.useMemo(
    () =>
      getLanguageOptionsSupported()
        .toArray()
        .map((lang) => ({
          value: lang.get('value'),
          label: lang.get('label'),
        })),
    [],
  )

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
        <CircularProgress size={16} sx={{ color: theme.palette.text.secondary }} />
        <Typography variant="body2" color="text.secondary">
          Loading translations...
        </Typography>
      </Box>
    )
  }

  if (error) {
    return (
      <Typography variant="body2" color="error" sx={{ mt: 2 }}>
        Error loading translations: {error.message}
      </Typography>
    )
  }

  const translationItems = languageOptions
    .filter((lang) => lang.value !== currentLanguage)
    .map((lang) => {
      const language = lang.value
      const isPrimary = primaryLanguage === language
      let languageText = lang.label
      if (isPrimary) {
        languageText += ' (Primary)'
      }

      const existingTranslation = translations.find(
        (t: { language?: string }) => t?.language === language,
      )

      let href: string | undefined =
        `/content/create?contentId=${contentId}&contentType=${contentType}`
      let text = 'Create Translation'
      let chipClass = 'language-chip--default'
      let isPublished: boolean | undefined

      if (isViewOnly) {
        href = undefined
        text = 'Does not exist'
      } else if (existingTranslation) {
        href = `/content/${existingTranslation.id}`
        text = existingTranslation.title || 'View Translation'
        chipClass = getLanguageChipClass(language)
        isPublished = existingTranslation.published ?? undefined
      }

      return {
        language,
        languageText,
        href,
        text,
        chipClass,
        isPublished,
      }
    })

  if (translationItems.length === 0) {
    return null
  }

  return (
    <Box className="translations">
      <Box component="div" className="translations__header">
        Translations
      </Box>
      <ul className="translations__list">
        {translationItems.map((item) => (
          <li key={`translation-${item.language}`} className="translations__item">
            {item.href ? (
              <Link href={item.href}>
                {item.isPublished !== undefined ? (
                  <Tooltip title={item.isPublished ? 'Published' : 'Unpublished'} arrow>
                    <Box
                      className={`translations__status-circle translations__status-circle--${item.isPublished ? 'published' : 'unpublished'}`}
                    />
                  </Tooltip>
                ) : (
                  <Box className="translations__status-circle translations__status-circle--default" />
                )}
                <Chip
                  label={item.languageText}
                  size="small"
                  variant="outlined"
                  className={`language-chip ${item.chipClass}`}
                  sx={{
                    mr: 1,
                    mb: 1,
                    color: '#333',
                    minWidth: '89px',
                    '&:hover': {
                      opacity: 0.8,
                    },
                  }}
                />
                <span
                  className={`translations__link-text ${item.text === 'Create Translation' ? 'translations__link-text--create' : 'translations__link-text--exists'}`}
                >
                  {item.text}
                </span>
              </Link>
            ) : (
              <>
                <Box className="translations__status-circle translations__status-circle--default" />
                <Chip
                  label={item.languageText}
                  size="small"
                  variant="outlined"
                  className={`language-chip ${item.chipClass}`}
                  sx={{
                    mr: 1,
                    mb: 1,
                    color: '#333',
                    minWidth: '89px',
                  }}
                />
                <span className="translations__link-text translations__link-text--disabled">
                  {item.text}
                </span>
              </>
            )}
          </li>
        ))}
      </ul>
    </Box>
  )
}

export default Translations
